import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, FileText, Scale, Shield, Users, AlertTriangle, Mail, Twitter, Instagram, Facebook, Github } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ThemeLogo } from '../components/ui/theme-logo';

const TermsOfService = () => {
  const location = useLocation();
  
  // Force scroll to top when this route is accessed
  useEffect(() => {
    // IMMEDIATE scroll - no delays
    const immediateScroll = () => {
      // Multi-method immediate scroll
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      
      // Also scroll any potential scrollable containers immediately
      const scrollableElements = Array.from(document.querySelectorAll('*')).filter(el => {
        const style = getComputedStyle(el);
        return style.overflow === 'scroll' || style.overflow === 'auto' || style.overflowY === 'scroll' || style.overflowY === 'auto';
      });
      
      scrollableElements.forEach(el => {
        if (el.scrollTop > 0) {
          el.scrollTop = 0;
        }
      });
    };
    
    // Execute immediately
    immediateScroll();
    
    // Backup attempts with minimal delay
    requestAnimationFrame(immediateScroll);
    setTimeout(immediateScroll, 1);
    setTimeout(immediateScroll, 10);
    
    // One final check after very short delay to ensure React rendered
    setTimeout(immediateScroll, 50);
  }, [location.pathname]); // Trigger on route change

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-white dark:from-background dark:to-gray-950">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-gradient-to-r from-background to-white dark:from-background dark:to-gray-950 shadow-md py-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <Link to="/" className="flex items-center">
              <ThemeLogo className="h-[42px]" />
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/#features" className="text-gray-600 dark:text-gray-300 hover:text-primary transition-colors">Features</Link>
              <Link to="/#how-it-works" className="text-gray-600 dark:text-gray-300 hover:text-primary transition-colors">How It Works</Link>
              <Link to="/#community" className="text-gray-600 dark:text-gray-300 hover:text-primary transition-colors">Community</Link>
              <Link to="/#pricing" className="text-gray-600 dark:text-gray-300 hover:text-primary transition-colors">Pricing</Link>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/auth/login">
                <Button variant="ghost" className="hidden sm:flex">Sign in</Button>
              </Link>
              <Link to="/auth/login">
                <Button className="bg-primary hover:bg-primary-hover text-white">
                  Get Started
                </Button>
            </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Link to="/" className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-primary transition-colors mb-6">
              <ArrowLeft size={16} className="mr-2" />
            Back to Home
          </Link>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-4xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6"
            >
              Terms of <span className="text-primary">Service</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto"
            >
              These terms govern your use of Nosh and describe the rights and responsibilities that apply to all users.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-6 text-sm text-gray-500 dark:text-gray-500"
            >
              Last updated: Jun 11, 2025
            </motion.div>
          </div>
        </div>
      </section>

      {/* Terms Overview */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-primary/5 rounded-2xl p-8 border border-primary/20 mb-12"
          >
            <div className="flex items-center mb-6">
              <div className="p-3 bg-primary/10 rounded-xl mr-4">
                <FileText className="h-8 w-8 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Agreement Overview</h2>
            </div>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              By using Nosh, you agree to these terms of service. Please read them carefully as they contain important 
              information about your rights and obligations when using our food waste reduction platform.
            </p>
          </motion.div>

          {/* Terms Sections */}
          <div className="space-y-12">
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-center mb-6">
                <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl mr-4">
                  <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Acceptance of Terms</h3>
              </div>
              <div className="space-y-4 text-gray-600 dark:text-gray-400">
            <p>
                  By accessing or using Nosh ("the Service"), you agree to be bound by these Terms of Service ("Terms"). 
                  If you disagree with any part of these terms, then you may not access the Service.
            </p>
            <p>
                  These Terms apply to all visitors, users, and others who access or use the Service. We reserve the right 
                  to update these Terms at any time, and your continued use of the Service constitutes acceptance of any changes.
                </p>
              </div>
            </motion.section>

            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-center mb-6">
                <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-xl mr-4">
                  <Shield className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Account Responsibilities</h3>
              </div>
              <div className="space-y-4 text-gray-600 dark:text-gray-400">
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Account Security</h4>
                  <p>You are responsible for safeguarding your account credentials and for all activities that occur under your account.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Accurate Information</h4>
                  <p>You agree to provide accurate, current, and complete information during registration and to update it as necessary.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Age Requirements</h4>
                  <p>You must be at least 13 years old to use this Service. Users under 18 must have parental consent.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">One Account Per User</h4>
                  <p>You may only create one account and may not share your account with others.</p>
                </div>
              </div>
            </motion.section>

            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-center mb-6">
                <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl mr-4">
                  <Scale className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Acceptable Use</h3>
              </div>
              <div className="space-y-4 text-gray-600 dark:text-gray-400">
                <p>You agree to use Nosh only for lawful purposes and in accordance with these Terms. You agree NOT to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Use the Service for any illegal or unauthorized purpose</li>
                  <li>Violate any local, state, national, or international law or regulation</li>
                  <li>Transmit any harmful, threatening, abusive, or defamatory content</li>
                  <li>Attempt to gain unauthorized access to other user accounts or our systems</li>
                  <li>Use automated systems to access the Service without permission</li>
                  <li>Interfere with or disrupt the Service or servers connected to the Service</li>
                  <li>Upload or share content that contains viruses or malicious code</li>
            </ul>
              </div>
            </motion.section>

            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-center mb-6">
                <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-xl mr-4">
                  <AlertTriangle className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Disclaimers and Limitations</h3>
              </div>
              <div className="space-y-4 text-gray-600 dark:text-gray-400">
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Food Safety</h4>
                  <p>
                    Nosh provides suggestions and information about food storage and consumption. However, you are solely responsible 
                    for making decisions about food safety. Always use your best judgment when consuming food, especially items near 
                    or past their expiry dates.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">AI-Generated Recipes and Content Disclaimer</h4>
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
                    <div className="flex items-start">
                      <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <p className="text-red-800 dark:text-red-200 font-semibold mb-2">IMPORTANT SAFETY NOTICE</p>
                        <p className="text-red-700 dark:text-red-300 text-sm">
                          All recipes, ingredient suggestions, and meal recommendations generated by our AI system are for 
                          informational purposes only and should not be considered professional dietary, nutritional, or medical advice.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3 text-sm">
                    <p>
                      <strong className="text-gray-900 dark:text-white">AI Limitations:</strong> Our AI system uses third-party 
                      APIs and artificial intelligence models that may generate inaccurate, incomplete, or potentially harmful 
                      recipe suggestions. AI systems can make mistakes and may not account for all safety considerations.
                    </p>
            <p>
                      <strong className="text-gray-900 dark:text-white">Allergy Warning:</strong> YOU ARE SOLELY RESPONSIBLE 
                      for verifying that all ingredients in AI-generated recipes are safe for consumption and do not contain 
                      allergens that may cause adverse reactions. Always double-check ingredients against your known allergies 
                      and dietary restrictions before cooking or consuming any recipe.
                    </p>
                    <p>
                      <strong className="text-gray-900 dark:text-white">Nutritional Information:</strong> Nutritional values, 
                      calorie counts, and dietary classifications (keto, vegan, etc.) provided by our AI are estimates only 
                      and may be inaccurate. Consult a registered dietitian or nutritionist for professional dietary guidance.
                    </p>
                    <p>
                      <strong className="text-gray-900 dark:text-white">Food Safety:</strong> Our AI cannot assess food 
                      freshness, spoilage, or safety. Always use your own judgment regarding food safety, expiration dates, 
                      and proper cooking temperatures. Follow established food safety guidelines from health authorities.
            </p>
                    <p>
                      <strong className="text-gray-900 dark:text-white">Third-Party Services:</strong> We utilize third-party 
                      AI services and APIs to generate content. We are not responsible for the accuracy, reliability, or 
                      safety of content generated by these external services.
                    </p>
            <p>
                      <strong className="text-gray-900 dark:text-white">No Liability:</strong> Nosh, its officers, directors, 
                      employees, and affiliates disclaim all liability for any illness, injury, allergic reaction, or other 
                      harm resulting from the use of AI-generated recipes or following AI-generated dietary suggestions. 
                      Use all AI-generated content at your own risk.
                    </p>
                    <p>
                      <strong className="text-gray-900 dark:text-white">User Responsibility:</strong> By using our AI features, 
                      you acknowledge that you understand these limitations and agree to independently verify all information 
                      before acting upon it. You assume full responsibility for your food choices and cooking decisions.
                    </p>
                  </div>
                  
                  <div className="mt-6">
                    <h5 className="font-semibold text-gray-900 dark:text-white mb-4">
                      Additional AI Content Risks & Disclaimers
                    </h5>
                    <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
                      <p>
                        <strong>AI-Generated Images:</strong> Our AI may generate food images that are inaccurate, misleading, 
                        offensive, inappropriate, or completely unrelated to food. Generated images may not accurately represent 
                        the actual recipe, ingredients, or final dish appearance. We disclaim all responsibility for inappropriate, 
                        offensive, or inaccurate visual content generated by AI systems.
                      </p>
                      <p>
                        <strong>Content Appropriateness:</strong> AI-generated content (recipes, images, text) may occasionally 
                        include inappropriate, offensive, biased, discriminatory, or culturally insensitive material. We do not 
                        endorse or take responsibility for such content and encourage users to report inappropriate material.
                      </p>
                      <p>
                        <strong>Intellectual Property:</strong> AI-generated content may inadvertently infringe upon copyrights, 
                        trademarks, or other intellectual property rights. Users assume all risk for any intellectual property 
                        violations arising from AI-generated content.
            </p>
            <p>
                        <strong>Data Privacy:</strong> When you upload images or provide text for AI processing, this data may 
                        be processed by third-party AI services. While we strive to protect your privacy, we cannot guarantee 
                        the privacy practices of external AI providers.
                      </p>
                      <p>
                        <strong>Service Reliability:</strong> AI services may experience outages, errors, or complete failures. 
                        We make no guarantees about AI service availability, response times, or quality of generated content.
            </p>
            <p>
                        <strong>Bias and Discrimination:</strong> AI systems may exhibit bias or generate discriminatory content 
                        based on their training data. We do not endorse any biased or discriminatory content that may be generated.
                      </p>
                      <p>
                        <strong>User-Generated Content:</strong> When you upload photos for AI analysis, you are responsible for 
                        ensuring the content is appropriate and that you have the right to upload it. Do not upload images of 
                        people without consent, private property, or inappropriate content.
                      </p>
                      <p>
                        <strong>Regulatory Compliance:</strong> AI technology is subject to evolving regulations. Users are 
                        responsible for ensuring their use of AI features complies with all applicable laws in their jurisdiction.
            </p>
                      <p>
                        <strong>No Warranty:</strong> ALL AI-GENERATED CONTENT IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. 
                        We expressly disclaim all warranties, express or implied, including but not limited to warranties of 
                        accuracy, reliability, fitness for a particular purpose, or non-infringement.
            </p>
            <p>
                        <strong>Complete Disclaimer:</strong> By using any AI features in our service, you acknowledge and agree 
                        that Nosh, its officers, directors, employees, affiliates, and third-party AI providers shall not be 
                        liable for any damages, losses, or harm of any kind arising from AI-generated content, including but not 
                        limited to direct, indirect, incidental, consequential, or punitive damages.
                      </p>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Service Availability</h4>
                  <p>
                    We strive to maintain continuous service availability but cannot guarantee uninterrupted access. 
                    The Service is provided "as is" without warranties of any kind.
            </p>
                </div>
              </div>
            </motion.section>

            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg border border-gray-200 dark:border-gray-700"
            >
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Intellectual Property</h3>
              <div className="space-y-4 text-gray-600 dark:text-gray-400">
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Our Content</h4>
                  <p>
                    The Service and its original content, features, and functionality are owned by Nosh and are protected 
                    by international copyright, trademark, and other intellectual property laws.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Your Content</h4>
                  <p>
                    You retain ownership of content you upload to the Service. By uploading content, you grant us a 
                    non-exclusive, worldwide, royalty-free license to use, display, and process your content to provide the Service.
            </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Restrictions</h4>
            <p>
                    You may not modify, distribute, or create derivative works based on the Service without our written permission.
                  </p>
                </div>
              </div>
            </motion.section>

            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg border border-gray-200 dark:border-gray-700"
            >
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Termination</h3>
              <div className="space-y-4 text-gray-600 dark:text-gray-400">
                <p>
                  We may terminate or suspend your account and access to the Service immediately, without prior notice or liability, 
                  for any reason, including breach of these Terms.
                </p>
                <p>
                  You may terminate your account at any time by contacting us or using the account deletion feature in your settings. 
                  Upon termination, your right to use the Service will cease immediately.
            </p>
                <p>
                  All provisions of these Terms that by their nature should survive termination shall survive, including 
                  ownership provisions, warranty disclaimers, and limitations of liability.
                </p>
              </div>
            </motion.section>

            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg border border-gray-200 dark:border-gray-700"
            >
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Limitation of Liability</h3>
              <div className="space-y-4 text-gray-600 dark:text-gray-400">
                <p>
                  In no event shall Nosh, its directors, employees, partners, agents, suppliers, or affiliates be liable for any 
                  indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or other 
                  intangible losses, resulting from your use of the Service.
            </p>
            <p>
                  Our total liability to you for all claims arising out of or relating to these Terms or your use of the Service 
                  shall not exceed the amount paid by you to us in the 12 months preceding the claim.
                </p>
              </div>
            </motion.section>

            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.9 }}
              className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg border border-gray-200 dark:border-gray-700"
            >
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Contact Information</h3>
              <div className="text-gray-600 dark:text-gray-400 space-y-4">
                <p>If you have questions about these Terms of Service, please contact us:</p>
                <div className="space-y-2">
                  <p><strong>Email:</strong> hello@noshhq.com</p>
                
                </div>
                <p>We'll respond to your inquiry within 30 days.</p>
              </div>
            </motion.section>
          </div>

          {/* Governing Law */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.0 }}
            className="mt-12 bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-8 border border-gray-200 dark:border-gray-700"
          >
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Governing Law</h3>
            <p className="text-gray-600 dark:text-gray-400">
              These Terms shall be governed by and construed in accordance with the laws of the State of California, 
              without regard to its conflict of law provisions. Any disputes arising from these Terms or your use of 
              the Service shall be resolved in the courts of San Francisco, California.
            </p>
        </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <Link to="/" className="flex items-center mb-4">
                <img src="/nosh-new-wh.png" alt="Nosh" className="h-[42px]" />
              </Link>
              <p className="text-gray-400 mb-6 max-w-md">
                Reduce food waste with AI-powered inventory management, recipe suggestions, and meal tracking.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <Twitter size={20} />
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <Instagram size={20} />
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <Facebook size={20} />
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <Github size={20} />
                </a>
            </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2">
                <li><Link to="/#features" className="text-gray-400 hover:text-white transition-colors">Features</Link></li>
                <li><Link to="/#how-it-works" className="text-gray-400 hover:text-white transition-colors">How It Works</Link></li>
                <li><Link to="/#pricing" className="text-gray-400 hover:text-white transition-colors">Pricing</Link></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">API</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2">
                <li><Link to="/help-center" className="text-gray-400 hover:text-white transition-colors">Help Center</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors">Contact Us</Link></li>
                <li><Link to="/privacy-policy" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms-of-service" className="text-gray-400 hover:text-white transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © 2025 Nosh. All rights reserved.
            </p>
            <div className="flex items-center mt-4 md:mt-0">
              <Mail size={16} className="mr-2 text-gray-400" />
              <a href="mailto:hello@noshhq.com" className="text-gray-400 hover:text-white transition-colors text-sm">
                hello@noshhq.com
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;